import { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Check, ContentCopy, Refresh } from '@mui/icons-material';

import SaraCircleMark from '../SaraCircleMark';

const ASSISTANT_TEXT_COLOR = '#123133';
const COPIED_RESET_DELAY_MS = 2000;

export type Props = {
    content: string;
    isLoading?: boolean;
    onRetry?: () => void;
};

const AssistantMessageBubble = ({ content, isLoading = false, onRetry }: Props) => {
    const [copied, setCopied] = useState(false);
    const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (copiedResetTimeoutRef.current) {
                clearTimeout(copiedResetTimeoutRef.current);
                copiedResetTimeoutRef.current = null;
            }
        },
        []
    );

    const handleCopy = useCallback(() => {
        navigator.clipboard
            .writeText(content)
            .then(() => {
                if (copiedResetTimeoutRef.current) {
                    clearTimeout(copiedResetTimeoutRef.current);
                    copiedResetTimeoutRef.current = null;
                }
                setCopied(true);
                copiedResetTimeoutRef.current = setTimeout(() => {
                    copiedResetTimeoutRef.current = null;
                    setCopied(false);
                }, COPIED_RESET_DELAY_MS);
            })
            .catch(() => {});
    }, [content]);

    return (
        <Stack alignItems="flex-start" direction="column" spacing={0.5} sx={{ maxWidth: '78%' }}>
            <SaraCircleMark size={24} variant="sara" />
            <Typography
                component="div"
                sx={{
                    color: ASSISTANT_TEXT_COLOR,
                    fontSize: 16,
                    letterSpacing: '0.15px',
                    lineHeight: '24px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {content}
            </Typography>
            <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%' }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                        aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
                        data-testid="assistant-message-copy"
                        onClick={handleCopy}
                        size="small"
                        sx={{ color: 'grey.600' }}
                    >
                        {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                    </IconButton>
                </Tooltip>
                {onRetry && (
                    <Tooltip title="Retry">
                        <span>
                            <IconButton
                                aria-label="Retry response"
                                data-testid="assistant-message-retry"
                                disabled={isLoading}
                                onClick={onRetry}
                                size="small"
                                sx={{ color: 'grey.600' }}
                            >
                                <Refresh sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>
        </Stack>
    );
};

export default AssistantMessageBubble;
